import { ArrowLeft, ArrowRight, Mic, Headphones, Radio, Users, Zap, Target, Volume2, RefreshCw } from 'lucide-react';
import './LandingPage.css';

export default function NarraTourPage({ onBack, onDemo }) {
  return (
    <div className="lp" data-testid="narratour-page">
      <div className="sp-hero sp-hero-green" data-testid="sp-hero">
        <button className="sp-back sp-back-right" onClick={onBack} data-testid="sp-back">
          Back to NicheSocial <ArrowRight size={16} />
        </button>
        <div className="sp-hero-inner">
          <div className="sp-hero-content">
            <span className="sp-badge">Included in Social Pro</span>
            <h1 className="sp-title">NarraTour</h1>
            <p className="sp-subtitle">
              Voice-cloned narration that brings every property to life. Engaging, personal,
              and created without the need for ongoing recording.
            </p>
            <p className="sp-body">
              Onboarding captures each agent's natural voice and delivery style. This session can
              take place in our studio or on-site at your offices. Our team brings professional
              recording equipment, an isolation booth, and auto-cue to capture voice perfectly.
            </p>
            <button className="sp-cta" onClick={onDemo} data-testid="sp-cta">
              Book a Demo <ArrowRight size={16} />
            </button>
          </div>
          <div className="sp-hero-visual">
            <div className="sp-icon-ring sp-icon-ring-green">
              <Mic size={48} />
            </div>
          </div>
        </div>
      </div>

      <section className="sp-section sp-section-grey" data-testid="sp-use-cases">
        <div className="sp-container">
          <h2 className="sp-h2">What agents use NarraTour for</h2>
          <div className="sp-use-grid">
            <div className="sp-use" data-testid="sp-use-tours">
              <h3>Narrated Property Tours</h3>
              <p>Every listing gets a professional voice-over tour in your agent's own voice. Warm, personal, and consistent across hundreds of properties.</p>
            </div>
            <div className="sp-use" data-testid="sp-use-market">
              <h3>Market Commentary</h3>
              <p>Voice-led market updates and area insights. Your agent's voice delivering expert commentary without needing to record a single take.</p>
            </div>
            <div className="sp-use" data-testid="sp-use-shy">
              <h3>Camera-Shy Agents</h3>
              <p>Not every agent is comfortable on camera. NarraTour lets them create engaging, personal video content using just their voice.</p>
            </div>
            <div className="sp-use" data-testid="sp-use-scale">
              <h3>Scale Without Effort</h3>
              <p>Once onboarded, every new listing automatically gets a narrated tour. No re-recording, no scheduling, just consistent quality.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-white" data-testid="sp-session">
        <div className="sp-container">
          <h2 className="sp-h2 sp-h2-green">The onboarding process</h2>
          <p className="sp-section-intro">
            Our team can come to your offices with professional recording equipment and an isolation booth,
            or agents can visit our studio. Whichever works best for your team.
          </p>
          <div className="sp-grid">
            <div className="sp-card" data-testid="sp-card-voice">
              <Mic size={20} className="sp-card-icon sp-icon-green" />
              <h3>Professional Voice Capture</h3>
              <p>Professional voice capture in a controlled environment. Either at our studio or on-site at your offices with our portable isolation booth.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-autocue">
              <Radio size={20} className="sp-card-icon sp-icon-green" />
              <h3>Guided Auto-Cue Delivery</h3>
              <p>Guided delivery using prepared auto-cue scripts so agents read naturally and consistently.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-tone">
              <Volume2 size={20} className="sp-card-icon sp-icon-green" />
              <h3>Tone and Pacing Calibration</h3>
              <p>Calibrated to match your brand style. Warm, professional, or authoritative. You choose the tone that fits.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-testing">
              <Headphones size={20} className="sp-card-icon sp-icon-green" />
              <h3>Real Property Testing</h3>
              <p>Tested with real property descriptions to make sure the voice clone sounds natural on actual listings.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-repeat">
              <RefreshCw size={20} className="sp-card-icon sp-icon-green" />
              <h3>Setup for Repeat Use</h3>
              <p>Once captured, your agent's voice is reused across every future listing. No re-recording needed.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-swap">
              <Users size={20} className="sp-card-icon sp-icon-green" />
              <h3>Agent Swap Available</h3>
              <p>Agent swap also available on fair use. Flexibility as your team evolves.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-green-solid" data-testid="sp-pricing">
        <div className="sp-container sp-pricing-block">
          <h2 className="sp-h2 sp-h2-white">NarraTour Onboarding</h2>
          <div className="sp-price-row">
            <span className="sp-price">£75</span>
            <span className="sp-price-unit">per agent</span>
          </div>
          <p className="sp-price-bulk">£50 per agent for 10+</p>
          <p className="sp-price-note">
            Once onboarded, your agents' voices can be reused across multiple properties. Save time
            while keeping content personal and consistent.
          </p>
          <p className="sp-price-extra">Agent swap also available (fair use applies).</p>
          <button className="sp-cta sp-cta-white" onClick={onDemo} data-testid="sp-pricing-cta">
            Book a Demo <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <footer className="lp-footer" data-testid="sp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <span className="lp-logo lp-logo-footer">
              <span className="lp-logo-niche">niche</span><span className="lp-logo-social">social</span>
            </span>
            <p className="lp-footer-powered">Powered by NicheCom property marketing assets.</p>
          </div>
          <nav className="lp-footer-links">
            <button onClick={onBack}>Back to NicheSocial</button>
          </nav>
        </div>
      </footer>
    </div>
  );
}
